import React from 'react';

import { toast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import { Label } from '@dashboard/components/Form';
import { Input } from '@dashboard/components/ui/input';
import { confirm } from '@dashboard/lib/confirm';
import { formatPrice } from '@dashboard/services/formatting';
import { useStuckFreeCharge } from '@dashboard/hooks/queries/useAdminQueries';
import { useFinalizeStuckFreeCharge } from '@dashboard/hooks/queries/useAdminMutations';

export default function FinalizeStuckFreeBooking() {
  const [chargeIdInput, setChargeIdInput] = React.useState('');
  const [chargeId, setChargeId] = React.useState('');

  const stuckFreeCharge = useStuckFreeCharge(chargeId);
  const state = stuckFreeCharge.data;

  const finalizeMutation = useFinalizeStuckFreeCharge({
    onSuccess: () => {
      toast({
        title: 'Booking finalized',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Error finalizing booking',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const onCheck = event => {
    event.preventDefault();
    const nextChargeId = chargeIdInput.trim();

    if (nextChargeId && nextChargeId === chargeId) {
      stuckFreeCharge.refetch();
      return;
    }

    setChargeId(nextChargeId);
  };

  const onFinalize = async () => {
    const confirmed = await confirm(
      `Finalize charge ${state.chargeId}? This marks it paid and sends its webhooks, tickets and confirmation emails.`,
    );

    if (!confirmed) {
      return;
    }

    finalizeMutation.mutate({ chargeId: state.chargeId });
  };

  const rows = state
    ? [
        ['Status', state.status],
        ['Amount', formatPrice({ amount: state.amount, currency: state.currency })],
        ['Page type', state.pageType],
        ['Mode', state.livemode ? 'Live' : 'Test'],
        ['Customer email', state.customerEmail],
        ['Transactions', state.transactions],
        ['Tickets', state.tickets],
        ['Active created webhooks', state.activeCreatedWebhooks],
        ['Later paid charges by same customer', state.laterPaidChargesBySameCustomer],
      ]
    : [];

  return (
    <div className="mt-4">
      <form onSubmit={onCheck}>
        <Label
          htmlFor="stuckFreeChargeId"
          description="Completes a free charge whose checkout request hung before it was finalized."
        >
          Finalize stuck free booking
        </Label>
        <div className="flex gap-x-2">
          <Input
            id="stuckFreeChargeId"
            value={chargeIdInput}
            onChange={event => setChargeIdInput(event.target.value)}
            placeholder="Charge ID"
            className="max-w-72"
          />
          <Button type="submit" appearance="secondary" disabled={!chargeIdInput.trim()}>
            Check
          </Button>
        </div>
      </form>

      {stuckFreeCharge.isError && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {stuckFreeCharge.error?.message}
        </p>
      )}

      {state && (
        <div className="mt-3 space-y-3">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-gray-500 dark:text-muted-foreground">{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          {!state.canFinalize && state.reason && (
            <p className="text-sm text-red-600 dark:text-red-400">{state.reason}</p>
          )}

          <Button
            onClick={onFinalize}
            appearance="primary"
            disabled={!state.canFinalize || finalizeMutation.isPending}
          >
            Finalize booking
          </Button>
        </div>
      )}
    </div>
  );
}
